import { memo } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { ActionButtonProps } from './interfaces';

const VARIANT_STYLES = {
  emerald: {
    active: { borderColor: '#d1fae5', bgcolor: '#ecfdf5', color: '#059669', boxShadow: '0 1px 2px rgba(16,185,129,0.1)' },
  },
  purple: {
    active: { borderColor: '#f3e8ff', bgcolor: '#faf5ff', color: '#9333ea', boxShadow: '0 1px 2px rgba(147,51,234,0.1)' },
  },
  blue: {
    active: { borderColor: '#dbeafe', bgcolor: '#eff6ff', color: '#2563eb', boxShadow: '0 1px 2px rgba(37,99,235,0.1)' },
  },
  red: {
    active: { borderColor: '#fee2e2', bgcolor: '#fef2f2', color: '#dc2626', boxShadow: '0 1px 2px rgba(220,38,38,0.1)' },
  },
};

export const ActionButton = memo<ActionButtonProps>(({
  onClick,
  isActive = false,
  disabled = false,
  variant = 'emerald',
  icon,
  label,
  title,
  hideOnSmall = false,
}) => {
  const variantStyle = VARIANT_STYLES[variant];

  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      aria-pressed={isActive}
      sx={{
        minHeight: 32,
        px: 1.25,
        borderRadius: '8px',
        transition: 'all 0.3s',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        fontWeight: 600,
        fontSize: '12px',
        whiteSpace: 'nowrap',
        border: '1px solid',
        ...(disabled
          ? {
              cursor: 'not-allowed',
              opacity: 0.4,
              borderColor: 'transparent',
              bgcolor: 'transparent',
              color: '#94a3b8',
              pointerEvents: 'none',
            }
          : isActive
            ? variantStyle.active
            : {
                borderColor: '#e2e8f0',
                bgcolor: 'white',
                color: '#475569',
                '&:hover': { bgcolor: '#f8fafc' },
                '&:active': { bgcolor: '#f1f5f9' },
              }),
      }}
    >
      {icon}
      <Typography
        component="span"
        sx={{
          fontSize: 'inherit',
          fontWeight: 'inherit',
          display: hideOnSmall
            ? { xs: 'none', lg: 'block' }
            : { xs: 'none', md: 'block' },
        }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
});

ActionButton.displayName = 'ActionButton';
